/**
 * The engine's fallback voice (R5; rebuilt under D24 context-aware teaches;
 * rebuilt again under D25 pack overrides): a COMPLETE built-in table of
 * generic lines, each claimable by the pack.
 *
 * @remarks
 * Resolution order per slot: the pack's `defaultResponses` override when
 * authored, else the built-in — so every game works with zero authoring and
 * any game can re-voice any refusal, teach, or acknowledgement
 * (`content-schema/default-responses.ts` documents the slots and their
 * placeholders). This is how Orpheus Protocol makes the ask deflection
 * EURYDICE's without the engine knowing she exists, and how any pack turns
 * its failure paths into character (the Zork "hollow voice" lineage, as a
 * platform feature).
 *
 * ABSENCE IS SILENCE still rules the content side: an item without
 * `askNarratives` gets the deflection SLOT — overriding the slot re-voices
 * the deflection, it never answers the question. The built-in texts are
 * engine-neutral by intent; register polish belongs to the content lane.
 *
 * Interpolation is deterministic: `{placeholder}` tokens substitute from the
 * call's interpolation map; unknown braces pass through untouched;
 * `{suggestions}` arrives with its own leading space when non-empty.
 *
 * @packageDocumentation
 */

import { type DefaultResponseSlot, isTopicIdKey, type NarrativeLine, type TopicDefinition } from '@orpheus/content-schema';
import { toEntityRef } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { describeEntityRef } from '../entities/describe-entity-ref.js';
import { roomIdForEntityRef, roomNameInPlayersWord } from '../entities/entity-ref.js';
import { BUILT_IN_RESPONSES } from './built-in-response-table.js';
import { articleFreeNameOf, dropArticleBefore } from './name-grammar.js';

// The name grammar (the article a name takes, the sentences the engine
// composes from names) lives beside this table since D229 — the file-size
// cap — and the two names every handler imported from here still resolve.
export { nameInSentence, offeredAnswersSentence } from './name-grammar.js';

/** The substitution values a slot's text may embed. */
export interface ResponseInterpolations {
	/** §8.8: the clause texts a stopped chain abandoned, joined for prose. */
	readonly abandoned?: string;
	/** D253: the thing whose shut see-through door stands between the hands and what it holds, as a sentence names it. */
	readonly containerName?: string;
	/** D212: the clause as the fuzzy rung read it, uppercased like a directive — what `correctionConfirm` asks about. */
	readonly correction?: string;
	/** D234: what a strike cost — the fact the fight's slots state, as a figure. */
	readonly damage?: string;
	/** D179: the locations walked — the number `restartConfirm` throws away, the MAP's own count. */
	readonly count?: string;
	/**
	 * D43: the directive listing, DERIVED from the compiled lexicon rather than
	 * written down — a built-in that hardcodes one game's verbs is a help screen
	 * that lies in every other game (Calder's Lane A finding).
	 */
	readonly directives?: string;
	/** D234: what is left after a strike — the player's, or the foe's. */
	readonly health?: string;
	readonly intent?: string;
	/** D38: the word joining an answer to its question (`with`, `on`). */
	readonly itemName?: string;
	readonly itemNames?: string;
	readonly joiner?: string;
	/** D229 (E16): the confirm's keeping answer, as a sentence says it — `restartAnswers`' "keep it". */
	readonly keepLabel?: string;
	/** D229 (E14): the answers a which-one ask offers, as one sentence (`offeredAnswersSentence` in `name-grammar.ts`). */
	readonly options?: string;
	/** D229 (E16): the confirm's proceeding answer, as a sentence says it — `restartAnswers`' "start over". */
	readonly proceedLabel?: string;
	readonly suggestions?: string;
	readonly tokens?: string;
	/**
	 * D38: the player's verb in SENTENCE case. `{intent}` shouts a directive
	 * name ("Nothing about the kettle answers to THROW"); `{verb}` belongs
	 * inside prose ("You cannot throw the kettle", "To take what?") —
	 * "THROW, on the kettle" is neither. D230 adds the third casing a sentence
	 * needs: `{Verb}`, the same word with its first letter up, for a line that
	 * OPENS with it ("Leave it where?"). It is never supplied — the
	 * interpolator derives it from `verb` wherever `verb` is offered.
	 */
	readonly verb?: string;
	/** D143: the thing already in the slot, when a second thing is offered for it. */
	readonly wornItemName?: string;
}

/** Narrow a slot value to a pool (Array.isArray widens readonly arrays to any — this keeps the types). */
const isResponsePool = (value: NarrativeLine | readonly NarrativeLine[]): value is readonly NarrativeLine[] =>
	Array.isArray(value);



/**
 * Resolve one response slot for one bundle: the pack's override when
 * authored, else the pack's words for the same act from elsewhere in its
 * document when the caller knows of some, else the built-in — interpolated
 * either way.
 *
 * @param bundle - The loaded bundle (its pack may claim slots)
 * @param slot - Which fallback is being spoken
 * @param interpolations - The slot's substitution values
 * @param timesSlotHasSpoken - How often this slot has spoken this session (a pool's cursor, D30)
 * @param packWordsElsewhere - D229 (E16): the pack's own sentence for this act, authored somewhere other than the slot — the title screen's `restart.confirm` for `restartConfirm` — spoken in the built-in's voice when the slot itself is unclaimed, so the same act never sounds like two
 * @returns The line to speak
 */
export const resolveEngineResponse = (
	bundle: EngineGameBundle,
	slot: DefaultResponseSlot,
	interpolations: ResponseInterpolations = {},
	timesSlotHasSpoken: number = 0,
	packWordsElsewhere?: string,
): NarrativeLine => {
	const builtIn = packWordsElsewhere === undefined ? BUILT_IN_RESPONSES[slot] : { ...BUILT_IN_RESPONSES[slot], text: packWordsElsewhere };
	const authored = bundle.contentPack.defaultResponses?.[slot] ?? builtIn;
	// D30: a pool plays in authored order and HOLDS on its last line — the
	// joke lands once, the workhorse teach carries the rest of the session.
	const line = isResponsePool(authored)
		? (authored[Math.min(timesSlotHasSpoken, authored.length - 1)] ?? authored[authored.length - 1])
		: authored;

	if (line === undefined) {
		// Unreachable by schema (pools are min(1)) — typed for the array index.
		throw new Error(`response slot "${slot}" resolved to an empty pool`);
	}

	// D74: authored text may be conditional — interpolation maps over EVERY
	// reading (fallback and variants alike), and CHOOSING a reading stays at
	// the stamping boundary, where the live state is.
	return { ...line, text: interpolateConditionalText(line.text, interpolations, articleFreeNameOf(bundle, interpolations.itemName)) };
};

/**
 * One spoken slot response: the line to speak plus WHICH slot spoke (D58).
 *
 * @remarks
 * The slot rides along so the seam that commits state can bump
 * `session.responseUsage` without re-deriving what was said — the same
 * shape {@link ParseTeachSelection} has carried since D30, now available to
 * every handler and refusal rather than only to the parse-teach path.
 */
export interface SpokenResponse {
	/** The line to speak, already resolved against the pool cursor. */
	readonly line: NarrativeLine;
	/** Which slot spoke — the committing seam owns the D30 usage increment. */
	readonly slot: DefaultResponseSlot;
}

/**
 * Speak one slot AGAINST THE SESSION'S POOL CURSOR, reporting which slot it
 * was (D58).
 *
 * @remarks
 * The pool-aware sibling of {@link resolveEngineResponse}, and the one every
 * handler and refusal should reach for: it reads this session's fire count so
 * an authored pool advances, and it returns the slot so the committing seam
 * (executeTurn's executed arm; runClauses' rejection arm) can record the
 * fire. `resolveEngineResponse` called bare reads count zero forever — which
 * is exactly the silently-held-at-line-one defect D31 refuses, and why D58
 * exists.
 *
 * `state` is optional because narrowing runs from tooling without one; absent,
 * the count is zero and the first pool line speaks, which is the only honest
 * answer a caller with no session can give.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state, when the caller has one
 * @param slot - Which slot is speaking
 * @param interpolations - The slot's substitution values
 * @returns The line and the slot that spoke it
 */
export const speakSlot = (
	bundle: EngineGameBundle,
	state: { readonly session: { readonly responseUsage: Readonly<Record<string, number>> }} | undefined,
	slot: DefaultResponseSlot,
	interpolations: ResponseInterpolations = {},
	packWordsElsewhere?: string,
): SpokenResponse => ({
	line: resolveEngineResponse(bundle, slot, interpolations, state?.session.responseUsage[slot] ?? 0, packWordsElsewhere),
	slot,
});

/**
 * A usage record with each spoken slot's count advanced by one (D58).
 *
 * @remarks
 * The write half of {@link speakSlot}: the seam that owns the state commits
 * this, once, for the slots the outcome reported. Split from the read half
 * because SPEAKING happens deep in handlers and narrowing while COMMITTING
 * belongs to the two seams that own state — `executeTurn`'s executed arm and
 * `runClauses`' rejection arm — and a function that did both would have to be
 * threaded everywhere state is not.
 *
 * @param responseUsage - The session's per-slot fire counts
 * @param spokenSlots - The slots one outcome spoke, in speaking order
 * @returns The advanced record (the same object when nothing spoke)
 */
export const bumpResponseUsage = (
	responseUsage: Readonly<Record<string, number>>,
	spokenSlots: undefined | readonly DefaultResponseSlot[],
): Readonly<Record<string, number>> =>
	spokenSlots === undefined || spokenSlots.length === 0
		? responseUsage
		: spokenSlots.reduce<Record<string, number>>(
			(advanced, slot) => ({ ...advanced, [slot]: (advanced[slot] ?? 0) + 1 }),
			{ ...responseUsage },
		);

/** One selected parse teach: the line to stamp plus which slot spoke. */
export interface ParseTeachSelection {
	/** The line to stamp. */
	readonly line: NarrativeLine;
	/** Which slot spoke — the caller commits the D30 usage increment. */
	readonly slot: DefaultResponseSlot;
}

/**
 * What the teach calls the thing an unknown verb was aimed at (D225, D227):
 * an item by its name; the hands by theirs; the room by the player's word for
 * it when that word was the room's own, and by `room itself` otherwise.
 *
 * @param bundle - The loaded bundle (item names)
 * @param diagnostics - The parse diagnostics, which may name an attempted object and the words used for it
 * @returns The name for the slot's `{itemName}`, or `undefined` when no thing was aimed at
 */
const attemptedThingNameOf = (
	bundle: EngineGameBundle,
	diagnostics: { readonly attemptedObjectRef?: string; readonly attemptedObjectSurface?: string },
): string | undefined => {
	if (diagnostics.attemptedObjectRef === undefined) {
		return undefined;
	}

	const entityRef = toEntityRef(diagnostics.attemptedObjectRef);

	return roomIdForEntityRef(entityRef) === undefined
		? describeEntityRef(bundle, entityRef)
		: roomNameInPlayersWord(diagnostics.attemptedObjectSurface);
};

/**
 * A parse failure, taught from what the parser UNDERSTOOD (D24): the
 * recognized head intent and its object policy select the slot; D25 lets the
 * pack re-voice every one of them; D30 plays pooled slots in sequence.
 *
 * @param bundle - The loaded bundle
 * @param diagnostics - The public parse diagnostics
 * @param timesSlotHasSpoken - Per-slot fire counts this session (`RuntimeState.session.responseUsage`)
 * @returns The teach to speak and the slot that spoke it
 */
export const parseTeachResponse = (
	bundle: EngineGameBundle,
	diagnostics: {
		readonly attemptedObjectRef?: string;
		/** D227: the words the player used for that thing, when the parser kept them. */
		readonly attemptedObjectSurface?: string;
		readonly attemptedVerbSurface?: string;
		/** D212: the clause as the fuzzy rung read it, on `verb_corrected`. */
		readonly correctedClauseText?: string;
		readonly failureKind: string;
		readonly nearMissSuggestions?: readonly string[];
		readonly resemblesLanguage?: boolean;
		readonly understoodIntentObjectPolicy?: 'forbidden' | 'optional' | 'required';
		readonly understoodIntentToken?: string;
		readonly unmatchedTokens: readonly string[];
		/** D48: the head intent came from a question-form alias (`what is`). */
		readonly wasQuestionFormIntent?: true;
	},
	timesSlotHasSpoken: Readonly<Record<string, number>> = {},
): ParseTeachSelection => {
	const tokens = diagnostics.unmatchedTokens.join(' ');
	const suggestions =
		diagnostics.nearMissSuggestions === undefined || diagnostics.nearMissSuggestions.length === 0
			? ''
			: ` Near misses: ${diagnostics.nearMissSuggestions.join(', ')}.`;
	// D36: an unknown-verb attempt quotes the word the PLAYER used as its verb.
	const intent = (diagnostics.attemptedVerbSurface ?? diagnostics.understoodIntentToken)?.toUpperCase();
	// D48 asked this only of a question form; D212 asks it of every miss with
	// words in it, because the answer picks the line for all of them: a thing
	// the pack declares somewhere is "not here", a word it never declared is
	// "what?". The scan is a short-circuiting walk over the pack's names and
	// runs only on a failure, so the executed turn pays nothing.
	// No tokens means nothing to have heard of — "the pack declares none of
	// these" would be vacuously true, so the missing-entity teach keeps the
	// empty case rather than the unknown register claiming it by default.
	const isUnknownToThePack = diagnostics.unmatchedTokens.length > 0 && !packDeclaresAnyOf(bundle, diagnostics.unmatchedTokens);
	const slot = selectParseTeachSlot(diagnostics, intent, tokens, isUnknownToThePack);
	// D225 (E5): the thing an unknown verb was aimed at is named TOTALLY —
	// an item by its name, the room by `room itself`, the hands by theirs
	// (D41, D63). This read `itemsById` alone and `?.name`d the miss, so
	// `wave to the skiff` at Salt Debt's lookout, where "skiff" is one of the
	// room's own nouns, left the slot unfilled and "Nothing about the
	// {itemName} answers to WAVE." reached the player. Every reference the
	// parser hands back came out of the catalog, which holds exactly those
	// three kinds, so the total description has no miss to default.
	// D227 (E5's leftover, Calder's `the-road-climbs` §3): the ROOM is named
	// by the player's own word when that word was the room's — `follow the
	// road` is refused about "the road" — exactly as E20 names it in a
	// refusal the engine built from a parsed command.
	const attemptedItemName = attemptedThingNameOf(bundle, diagnostics);
	const interpolations: ResponseInterpolations = {
		...(diagnostics.correctedClauseText === undefined ? {} : { correction: diagnostics.correctedClauseText.toUpperCase() }),
		suggestions,
		...(intent === undefined ? {} : { verb: intent.toLowerCase() }),
		tokens: slot === 'structureFallback' && intent === undefined ? (tokens === '' ? '' : ` (stuck on "${tokens}")`) : tokens,
		...(intent === undefined ? {} : { intent }),
		...(attemptedItemName === undefined ? {} : { itemName: attemptedItemName }),
	};

	return {
		line: resolveEngineResponse(bundle, slot, interpolations, timesSlotHasSpoken[slot] ?? 0),
		slot,
	};
};

/**
 * Does this pack declare ANYTHING that answers to one of these words (D48)?
 *
 * @remarks
 * The whole authored world, not the current room — that is the point. The
 * parser already told us the words resolve to nothing HERE; the question this
 * answers is whether they were ever a thing anywhere, which is what separates
 * "the kettle is in another room" from "nothing was ever called happening".
 *
 * Names are split on whitespace so a multiword name or alias ("pry bar")
 * matches on either word, which is how a player refers to it anyway. The scan
 * short-circuits and its result is a boolean, so map iteration order cannot
 * affect the answer — determinism is unaffected.
 *
 * D226 (E7): a room's own `nouns` count beside its name. Drumline's track
 * answers to `village`, and from the hollow `ask Sylvain about the village`
 * read "What village?" — the world pretending to lack a word its own room
 * declares. "You see no village here." is the true sentence there.
 *
 * @param bundle - The loaded bundle (the authored world)
 * @param tokens - The words the catalog could not place
 * @returns Whether any item, actor or room answers to any of them
 */
const packDeclaresAnyOf = (bundle: EngineGameBundle, tokens: readonly string[]): boolean => {
	const soughtWords = new Set(tokens.map((token) => token.toLowerCase()));
	const declaresAny = (phrases: readonly string[]): boolean =>
		phrases.some((phrase) => phrase.toLowerCase().split(/\s+/u).some((word) => soughtWords.has(word)));

	// D231: a topic's plain keys are words the pack declares a subject by —
	// `take the directive` is "You see no directive here", never "What
	// directive?", because the pack knows the word (the voice answers to it).
	for (const item of bundle.indexes.itemsById.values()) {
		if (declaresAny([item.name, ...item.nouns, ...item.aliases, ...plainTopicKeysOf(item.topics)])) {
			return true;
		}
	}

	// D232: a body is declared by its words as a thing is — `examine the
	// watchman` from the next room is "You see no watchman here".
	if ([...bundle.indexes.actorsById.values()].some((actor) => declaresAny([actor.name, ...actor.nouns, ...actor.aliases, ...plainTopicKeysOf(actor.topics)]))) {
		return true;
	}

	return [...bundle.indexes.roomsById.values()].some((room) => declaresAny([room.name, ...room.nouns, ...plainTopicKeysOf(room.topics)]));
};

/**
 * D231: the plain (non-id) keys of a topic table — the words it declares.
 *
 * @param topics - A thing's table, or nothing
 * @returns The keys that are words
 */
const plainTopicKeysOf = (topics: undefined | readonly TopicDefinition[]): readonly string[] =>
	(topics ?? []).flatMap((topic) => topic.keys.filter((key) => !isTopicIdKey(key)));

/**
 * The misses that name their slot outright — no diagnostics to weigh.
 * `blank_input` and the three register kinds are D24/D28; `verb_corrected`
 * is D212's ask, which must never run.
 */
const SLOT_BY_FAILURE_KIND: ReadonlyMap<string, DefaultResponseSlot> = new Map([
	['blank_input', 'blankInput'],
	['question_not_understood', 'questionNotUnderstood'],
	['social_input', 'socialInput'],
	['statement_not_understood', 'statementNotUnderstood'],
	['verb_corrected', 'correctionConfirm'],
]);

/**
 * The D24/D28/D29 slot-selection ladder, pure and total.
 *
 * @param diagnostics - The public parse diagnostics
 * @param intent - The understood head intent, uppercased (when any)
 * @param tokens - The unmatched tokens, joined
 * @param isUnknownToThePack - D48/D212: the unmatched words name nothing the pack ever declared
 * @returns The slot that should speak
 */
const selectParseTeachSlot = (
	diagnostics: {
		readonly attemptedObjectRef?: string;
		readonly failureKind: string;
		readonly resemblesLanguage?: boolean;
		readonly understoodIntentObjectPolicy?: 'forbidden' | 'optional' | 'required';
		readonly wasQuestionFormIntent?: true;
	},
	intent: string | undefined,
	tokens: string,
	isUnknownToThePack: boolean,
): DefaultResponseSlot => {
	const namedSlot = SLOT_BY_FAILURE_KIND.get(diagnostics.failureKind);

	if (namedSlot !== undefined) {
		return namedSlot;
	}

	if (diagnostics.failureKind === 'entity_not_found') {
		// D48: a question-form alias (`what is`) makes a question PARSE, so an
		// unanswerable one lands here instead of at the shape ladder. The
		// distinction that matters is not grammatical but FACTUAL — does the pack
		// declare anything by that name?
		//
		//   "what is the kettle" → the kettle is real and elsewhere → "There is
		//                          no kettle here" is the useful answer.
		//   "what is happening"  → nothing in the game was ever called that, so
		//                          it was question grammar, not a naming attempt.
		//
		// D212 extends the same fact to a VERB aimed at a miss: `take mask` two
		// floors above the mask is "You see no mask here"; `take zeppelin` is
		// "What zeppelin?" — the world does not deny a thing it has, and does
		// not pretend to a thing it lacks.
		//
		// The parser cannot make this call: it sees only the per-turn catalog,
		// which is exactly the set of things that ARE here.
		if (!isUnknownToThePack) {
			return 'entityNotFound';
		}

		return diagnostics.wasQuestionFormIntent === true ? 'questionNotUnderstood' : 'entityUnknown';
	}

	if (diagnostics.failureKind === 'unsupported_action') {
		// D36: a verb the vocabulary never declared, aimed at something the
		// catalog knows — refuse by name, on the same slots D35 established.
		return diagnostics.attemptedObjectRef === undefined ? 'unsupportedIntent' : 'unsupportedIntentOnTarget';
	}

	if (diagnostics.failureKind === 'no_intent_recognized') {
		// D28: register split — keyboard mash gets its own slot; words that ARE
		// language (just not directives) keep the honest teach. D212: language
		// that names a thing the pack declares somewhere is the entity miss
		// ("You see no kettle here."); language naming nothing the pack knows
		// keeps its own slot.
		if (diagnostics.resemblesLanguage !== true) {
			return 'gibberishInput';
		}

		return isUnknownToThePack ? 'noIntentRecognized' : 'entityNotFound';
	}

	if (diagnostics.failureKind === 'unbound_preposition' && intent !== undefined) {
		return 'unboundPreposition';
	}

	// structure_not_understood, with whatever the grammar DID recognize (D24).
	if (intent !== undefined) {
		if (diagnostics.understoodIntentObjectPolicy === 'forbidden' && tokens !== '') {
			return 'verbStandsAlone';
		}

		if (diagnostics.understoodIntentObjectPolicy === 'required' && tokens === '') {
			return 'verbNeedsTarget';
		}

		return 'verbCouldNotCarry';
	}

	return 'structureFallback';
};

/**
 * Substitute `{placeholder}` tokens deterministically; unknown braces pass
 * through.
 *
 * @remarks
 * Empty substitutions are SWEPT: a slot whose `{joiner}` or `{tokens}` came
 * back empty would otherwise leave a double space mid-sentence — or a space
 * marooned before a full stop — which is the kind of defect that only shows up
 * in the one transcript nobody pinned. The sweep collapses internal runs,
 * closes the gap before terminal punctuation, and trims the ends, so an
 * authored double space is not preserved: deliberate, and cheap next to the
 * alternative. It is pure string work, so replay determinism is untouched.
 */
const interpolateText = (text: string, interpolations: ResponseInterpolations, articleFreeName: string | undefined): string =>
	// D182: an article-free name takes the place of "the {itemName}" first,
	// so the general pass below never sees the article. A bare `{itemName}`
	// with no article before it is filled by that pass as ever.
	(articleFreeName === undefined ? text : dropArticleBefore(text, articleFreeName))
		.replace(
			/\{(abandoned|containerName|correction|count|damage|directives|health|intent|itemName|itemNames|joiner|keepLabel|options|proceedLabel|suggestions|tokens|Verb|verb|wornItemName)\}/gu,
			(wholeMatch, placeholderName: string) => {
				// D230: `{Verb}` is `{verb}` opening a sentence — one source word, three casings.
				const substitution =
					placeholderName === 'Verb' ? sentenceOpeningOf(interpolations.verb) : interpolations[placeholderName as keyof ResponseInterpolations];

				return substitution ?? wholeMatch;
			},
		)
		.replaceAll(/ {2,}/gu, ' ')
		.replaceAll(/ +(?=[!,.:;?])/gu, '')
		.trim();

/**
 * A word with its first letter up, for the start of a sentence — or nothing
 * when there is no word, so the placeholder stays visibly unfilled (D230).
 *
 * @param word - The word as typed, lowercased
 * @returns The word opening a sentence, or `undefined`
 */
const sentenceOpeningOf = (word: string | undefined): string | undefined =>
	word === undefined ? undefined : `${word.charAt(0).toUpperCase()}${word.slice(1)}`;

/**
 * Apply placeholder interpolation to every reading of a (possibly
 * conditional) authored text (D74).
 *
 * @param text - The authored text, plain or conditional
 * @param interpolations - The slot's substitution values
 * @returns The same shape with every reading interpolated
 */
const interpolateConditionalText = (
	text: NarrativeLine['text'],
	interpolations: ResponseInterpolations,
	articleFreeName: string | undefined,
): NarrativeLine['text'] =>
	typeof text === 'string'
		? interpolateText(text, interpolations, articleFreeName)
		: {
			fallback: interpolateText(text.fallback, interpolations, articleFreeName),
			variants: text.variants.map((variant) => ({
				condition: variant.condition,
				text: interpolateText(variant.text, interpolations, articleFreeName),
			})),
		};
